<template>
  <div id="app" class="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
    <header class="app-header">
      <div class="header-container">
        <div class="header-brand">
          <svg class="logo-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h1 class="logo-text">{{ $t('app.title') }}</h1>
        </div>

        <LanguageSwitcher />
      </div>
    </header>

    <main class="app-main">
      <div class="container">
        <div class="hero-section">
          <p class="hero-subtitle">{{ $t('app.subtitle') }}</p>
          <div class="privacy-badge">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <span>{{ $t('app.privacy') }}</span>
          </div>
        </div>

        <div class="tabs">
          <button
            @click="activeTab = 'split'"
            class="tab"
            :class="{ active: activeTab === 'split' }"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
              />
            </svg>
            {{ $t('app.split') }}
          </button>

          <button
            @click="activeTab = 'merge'"
            class="tab"
            :class="{ active: activeTab === 'merge' }"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z"
              />
            </svg>
            {{ $t('app.merge') }}
          </button>
        </div>

        <div class="tab-content">
          <PdfSplitter v-if="activeTab === 'split'" />
          <PdfMerger v-if="activeTab === 'merge'" />
        </div>
      </div>
    </main>

    <footer class="app-footer">
      <p class="footer-text">
        Built with Vue.js + Vite + Tailwind CSS • 100% Client-side
      </p>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import LanguageSwitcher from './components/LanguageSwitcher.vue'
import PdfSplitter from './components/PdfSplitter.vue'
import PdfMerger from './components/PdfMerger.vue'

const activeTab = ref<'split' | 'merge'>('split')
</script>

<style scoped>
.app-header {
  @apply bg-white shadow-sm;
}

.header-container {
  @apply max-w-6xl mx-auto px-4 sm:px-6 lg:px-8;
  @apply flex items-center justify-between;
  @apply py-4;
}

.header-brand {
  @apply flex items-center gap-3;
}

.logo-icon {
  @apply w-8 h-8 text-indigo-600;
}

.logo-text {
  @apply text-xl font-bold text-gray-900;
}

.app-main {
  @apply py-12;
}

.container {
  @apply max-w-4xl mx-auto px-4 sm:px-6 lg:px-8;
}

.hero-section {
  @apply text-center mb-10;
}

.hero-subtitle {
  @apply text-lg text-gray-600 mb-4;
}

.privacy-badge {
  @apply inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium;
}

.privacy-badge svg {
  @apply w-4 h-4;
}

.tabs {
  @apply flex gap-2 mb-8 bg-white rounded-lg p-1 shadow-sm;
}

.tab {
  @apply flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200;
  @apply text-gray-600 hover:text-gray-900 hover:bg-gray-50;
}

.tab svg {
  @apply w-5 h-5;
}

.tab.active {
  @apply bg-indigo-600 text-white;
  @apply shadow-md;
}

.tab-content {
  @apply pb-8;
}

.app-footer {
  @apply py-8 text-center border-t border-gray-200 mt-12;
}

.footer-text {
  @apply text-sm text-gray-500;
}
</style>
